/**
 * Authentication Guard - Role-based Access Control
 *
 * Dual mode: Keycloak (redirect) / Mock (localStorage).
 * При KC mode: использует KeycloakService.isLoggedIn() + getUserRoles().
 * При Mock mode: использует AuthService.isAuthenticated() + hasRole().
 *
 * KeycloakService инжектится как Optional — не падает при mock mode.
 *
 * @story 16.1
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService, UserRole } from '../services/auth.service';
import { KeycloakService } from 'keycloak-angular';

/**
 * Получить KeycloakService безопасно (null при mock mode)
 */
function getKeycloakSafe(): KeycloakService | null {
  if (!environment.useKeycloakAuth) return null;
  try {
    return inject(KeycloakService);
  } catch {
    return null;
  }
}

/**
 * Проверить аутентификацию с учётом mode (ASYNC version)
 * CRITICAL FIX: keycloak.isLoggedIn() должен быть async!
 */
async function checkAuth(
  authService: AuthService,
  keycloak: KeycloakService | null,
  router: Router,
  redirectUrl: string
): Promise<boolean> {
  if (environment.skipAuth && !environment.production) {
    return true;
  }

  if (environment.useKeycloakAuth && keycloak) {
    // CRITICAL FIX: ASYNC isLoggedIn() - ждём завершения token проверки
    const isLoggedIn = await keycloak.isLoggedIn();
    if (isLoggedIn) {
      return true;
    }
    keycloak.login({ redirectUri: window.location.origin + redirectUrl });
    return false;
  }

  if (authService.isAuthenticated()) {
    return true;
  }
  router.navigate(['/auth/login'], { queryParams: { returnUrl: redirectUrl } });
  return false;
}

/**
 * Basic authentication guard (ASYNC version)
 * CRITICAL FIX: Поддержка async checkAuth() для правильной Keycloak проверки
 */
export const AuthGuard: CanActivateFn = async (_route: ActivatedRouteSnapshot, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const keycloak = getKeycloakSafe();

  return await checkAuth(authService, keycloak, router, state.url);
};

/**
 * Role-based guard factory (ASYNC version)
 * CRITICAL FIX: Поддержка async checkAuth()
 */
export function RoleGuard(allowedRoles: UserRole[]): CanActivateFn {
  return async (_route: ActivatedRouteSnapshot, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const keycloak = getKeycloakSafe();

    const isAuthenticated = await checkAuth(authService, keycloak, router, state.url);
    if (!isAuthenticated) {
      return false;
    }

    if (authService.hasRole(allowedRoles)) {
      return true;
    }

    router.navigate(['/dashboard']);
    return false;
  };
}

/**
 * Permission-based guard factory (ASYNC version)
 * CRITICAL FIX: Поддержка async checkAuth()
 */
export function PermissionGuard(requiredPermissions: string[], requireAll = false): CanActivateFn {
  return async (_route: ActivatedRouteSnapshot, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const keycloak = getKeycloakSafe();

    const isAuthenticated = await checkAuth(authService, keycloak, router, state.url);
    if (!isAuthenticated) {
      return false;
    }

    const hasPermission = requireAll
      ? authService.hasAllPermissions(requiredPermissions)
      : authService.hasAnyPermission(requiredPermissions);

    if (hasPermission) {
      return true;
    }

    router.navigate(['/dashboard']);
    return false;
  };
}

/**
 * Admin-only guard
 */
export const AdminGuard: CanActivateFn = RoleGuard([UserRole.ADMIN]);

/**
 * Manager+ guard (admin or manager)
 */
export const ManagerGuard: CanActivateFn = RoleGuard([UserRole.ADMIN, UserRole.MANAGER]);

/**
 * Operator+ guard (admin, manager, or operator)
 */
export const OperatorGuard: CanActivateFn = RoleGuard([
  UserRole.ADMIN,
  UserRole.MANAGER,
  UserRole.OPERATOR,
]);
